"use client";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { LoaderCircle, Plus } from "lucide-react";
import { useInteractions } from "./hooks/interactionsProvider";
import InteractionsList from "./interactionsList/interactionsList";
import VideoWorkflowStep from "../videoWorkflow/videoWorkflowStep";
import { useVideoWorkflow } from "../videoWorkflow/videoWorkflowProvider";
import { Dispatch, SetStateAction } from "react";
import { getLanguageLabel, t } from "@/lib/localization";
import { SrclangType } from "@/db/schema";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useLocale } from "@/hooks/useLocale";

export default function InteractionsTab({
    setReloadVideoKey,
}: {
    setReloadVideoKey: Dispatch<SetStateAction<number>>;
}) {
    const {
        createInteraction,
        saveChanges,
        changes,
        loading,
        selectedSrclang,
        setSelectedSrclang,
        availableSrclangs = [],
    } = useInteractions();
    const { shouldShowWorkflowStepComponent } = useVideoWorkflow();
    const { dir } = useLocale();

    const hasInteractions = availableSrclangs.length > 0;

    return (
        <div className="mt-4">
            <div className="flex flex-row gap-2 justify-between items-center">
                <div>
                    <Label className="">
                        {t("video-sheet.interactions-tab.title")}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                        {t("video-sheet.interactions-tab.description")}
                    </p>
                </div>
                <Button
                    size="sm"
                    variant="outline"
                    className="flex flex-row items-center gap-1 w-fit"
                    onClick={createInteraction}
                >
                    <Plus className="size-4" />
                    {t("video-sheet.interactions-tab.add-btn")}
                </Button>
            </div>

            {hasInteractions && availableSrclangs.length > 1 && (
                <div className="my-2 flex">
                    <div className="shrink-0 flex justify-center items-center bg-muted text-sm h-9 border border-input px-2.5 rounded-s-md">
                        {t("video-sheet.interactions-tab.language-label")}
                    </div>
                    <Select
                        dir={dir}
                        value={selectedSrclang}
                        onValueChange={(value) =>
                            setSelectedSrclang?.(value as SrclangType)
                        }
                    >
                        <SelectTrigger className="rounded-s-none border-s-0 h-9 w-fit min-w-[140px]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {availableSrclangs.map((srclang) => (
                                <SelectItem key={srclang} value={srclang}>
                                    {getLanguageLabel(srclang)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            )}

            <div className="flex flex-col gap-6">
                <InteractionsList />

                {changes && (
                    <Button
                        disabled={loading || !changes}
                        onClick={saveChanges}
                        className="w-fit"
                    >
                        {loading ? (
                            <LoaderCircle className="size-4 animate-spin" />
                        ) : (
                            t("video-sheet.interactions-tab.save-btn")
                        )}
                    </Button>
                )}

                {shouldShowWorkflowStepComponent("interactions") && (
                    <VideoWorkflowStep
                        stepName="interactions"
                        texts={{
                            title: {
                                keyName:
                                    "video-sheet.create-interactions-with-ai.title",
                                defaultValue: "יצירת שאלות אינטראקטיביות עם AI",
                            },
                            description: {
                                keyName:
                                    "video-sheet.create-interactions-with-ai.description",
                                defaultValue:
                                    "לחצו כאן ובתוך כמה שניות ניצור לכם שאלות אינטראקטיביות על הסרטון",
                            },
                            generateBtn: {
                                keyName:
                                    "video-sheet.generate-interactions-with-ai-btn",
                                defaultValue: "יצירה",
                            },
                        }}
                    />
                )}
            </div>
        </div>
    );
}
